import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { User, UserPermission, UserRole } from '../../../users/ model/user.model';

import { LocalStorageService, SignalStore, forceArray } from 'bcte-common-lib';
import { API_CONFIG, ApiConfig } from '../../../core/models/api-config.model';
import { AuthResponse, ChangePasswordRequestData, ForgotPasswordRequestData, LoginRequestData, SignupRequestData } from '../models/auth.model';

/** Authentication store state */
interface AuthState {
  user: User | null;
  token: string | null;
  isSidenavOpen: boolean;
}

/** Initial authentication state */
function createInitialState(): AuthState {
  return { user: null, token: null, isSidenavOpen: true };
}

/**
 * Handles all authentication related functions
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  /** State store */
  private authStore: SignalStore<AuthState> = new SignalStore({ name: 'auth' }, createInitialState());

  /** Selectors */
  public user$ = this.authStore.select(state => state.user);
  public isLoggedIn$ = this.authStore.select(state => !!state.user);
  public isGuest$ = this.authStore.select(state => state.user?.role === UserRole.Guest);
  public isCustomer$ = this.authStore.select(state => state.user?.role === UserRole.Customer);
  public isInternalUser$ = this.authStore.select(state => state.user?.role === UserRole.InternalUser);
  public isAdmin$ = this.authStore.select(state => state.user?.role === UserRole.Admin);
  public isSidenavOpen$ = this.authStore.select(state => state.isSidenavOpen);

  constructor(
    private http: HttpClient,
    private storage: LocalStorageService,
    @Inject(API_CONFIG) private config: ApiConfig
  ) {}

  /**
   * Validate current user
   */
  public validate(): Observable<boolean> {
    // Skip if user is already logged in
    if (this.isLoggedIn$()) {
      return of(true);
    }

    // Ensure user token exists in cache
    const userToken = this.storage.getItem('user');
    if (!userToken) {
      return of(false);
    }

    // Add user token to store
    this.authStore.update({ token: userToken });

    // Validate user
    return this.http.get<User>(`${this.config.apiUrl}/users/me`).pipe(
      map(user => {
        this.authStore.update({ user });
        return true;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  /**
   * Reset state
   */
  public reset(): void {
    this.authStore.setState(createInitialState());
    this.storage.removeItem('user');
  }

  /**
   * Get user JWT token from cache
   * @returns user jwt token
   */
  public getUserToken(): string | null {
    return this.authStore.get(state => state.token);
  }

  /**
   * Determine if current user has any of the provided roles
   * @param roleOrRoles roles to check
   * @returns whether user has one or more provided roles
   */
  public hasAnyRole(roleOrRoles: UserRole | UserRole[]): boolean {
    const roles = forceArray(roleOrRoles);
    const user = this.user$();

    return roles.includes(user?.role);
  }

  /**
   * Determine if current user has any of the provided permissions
   * @param permissionOrPermissions permissions to check
   * @returns whether user has one or more provided permissions
   */
  public hasAnyPermission(permissionOrPermissions: UserPermission | UserPermission[]) {
    const permissions = forceArray(permissionOrPermissions);
    const user = this.user$();

    return permissions.some(p => user.permissions?.includes(p));
  }

  /** Get logged in user's full name */
  public getFullName(): string {
    const user = this.user$();

    return user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : '';
  }

  /**
   * Attempt to authenticate the user
   * @param requestData login data
   * @returns login status response
   */
  public login(requestData: LoginRequestData): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.config.apiUrl}/auth/login`, requestData).pipe(
      tap(res => {
        this.storage.setItem('user', res.token);
      })
    );
  }

  /**
   * Attempt to sign up a new user
   * @param requestData signup data
   * @returns signup status response
   */
  public signup(requestData: SignupRequestData): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.config.apiUrl}/auth/signup`, requestData);
  }

  /**
   * Attempt to change password
   * @param requestData change password data
   * @returns change password status response
   */
  public changePassword(requestData: ChangePasswordRequestData): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.config.apiUrl}/auth/change-password`, requestData);
  }

  /**
   * Send forgot password request
   * @param requestData Forgot password data
   */
  public forgotPassword(requestData: ForgotPasswordRequestData): Observable<void> {
    return this.http.post<void>(`${this.config.apiUrl}/auth/forgot-password`, requestData);
  }

  /**
   * Logout user from app
   */
  public logout(): void {}
}
